import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DevicesService } from 'src/app/features/main-application/_services/entities/devices.service';

@Component({
  selector: 'app-subscription-swop-device-dialog',
  templateUrl: './subscription-swop-device-dialog.component.html',
  styleUrls: ['./subscription-swop-device-dialog.component.scss']
})
export class SubscriptionSwopDeviceDialogComponent implements OnInit {
  availableDeviceOptions: Array<{ label: string; serial: string; device: any }> = [];
  isLoadingDevices = false;
  deviceLoadError: string | null = null;
  constructor(
    private fb: FormBuilder,
    public config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private devicesService: DevicesService
  ) {}

  deviceSwopForm: FormGroup<any> = this.fb.group({
    subscription_id: ['', Validators.required],
    current_device_serial_number: [{ value: '', disabled: true }],
    new_device_serial_number: ['', Validators.required]
  });

  ngOnInit(): void {
    const customerCircuit = this.config?.data?.customerCircuit;

    if (customerCircuit) {
      const subscriptionId = customerCircuit.subscription_id || customerCircuit.id;
      const currentDeviceSerial = this.extractDeviceSerialNumber(customerCircuit);

      this.deviceSwopForm.patchValue({
        subscription_id: subscriptionId,
        current_device_serial_number: currentDeviceSerial
      });
    }

    this.loadAvailableDevices();
  }

  onDeviceSwopSubmit() {
    if (this.deviceSwopForm.invalid) {
      return;
    }

    this.ref.close(this.deviceSwopForm.getRawValue());
  }

  private extractDeviceSerialNumber(customerCircuit: any): string {
    return customerCircuit?.device_serial_no || customerCircuit?.serial_no || '';
  }

  private loadAvailableDevices(queryParams: Record<string, any> = {}): void {
    this.isLoadingDevices = true;
    this.deviceLoadError = null;

    this.devicesService.getDevices({ limit: 50, ...queryParams }).subscribe({
      next: (res: any) => {
        const devices = this.extractDevicesFromResponse(res);
        const availableDevices = devices
          .filter((device) => this.isDeviceAvailable(device))
          .sort((a, b) => this.getDeviceUpdatedTimestamp(b) - this.getDeviceUpdatedTimestamp(a))
          .slice(0, 50)
          .map((device) => ({
            serial: this.resolveSerialNumber(device),
            label: this.buildDeviceLabel(device),
            device
          }))
          .filter((option) => !!option.serial);

        this.availableDeviceOptions = availableDevices;
        this.isLoadingDevices = false;
      },
      error: (error: any) => {
        console.error('Failed to load available devices', error);
        this.deviceLoadError = 'Unable to load available devices. Please try again later.';
        this.isLoadingDevices = false;
      }
    });
  }

  onDeviceFilter(rawFilter: string | undefined): void {
    const filterValue = rawFilter?.trim() || '';

    if (filterValue.length === 0) {
      this.loadAvailableDevices();
      return;
    }

    if (filterValue.length < 3) {
      return;
    }

    this.loadAvailableDevices({ q: filterValue });
  }

  private extractDevicesFromResponse(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.results)) {
      return response.results;
    }

    if (Array.isArray(response?.body?.data)) {
      return response.body.data;
    }

    return [];
  }

  private isDeviceAvailable(device: any): boolean {
    if (!device) {
      return false;
    }

    if (typeof device.available === 'boolean') {
      return device.available;
    }

    if (typeof device.status === 'string') {
      return device.status.toLowerCase() === 'available';
    }

    return false;
  }

  private getDeviceUpdatedTimestamp(device: any): number {
    const dateFields = ['date_updated'];

    for (const field of dateFields) {
      const value = device?.[field];
      const timestamp = this.parseDate(value);
      if (timestamp) {
        return timestamp;
      }
    }

    const fallbackFields = ['date_created'];
    for (const field of fallbackFields) {
      const value = device?.[field];
      const timestamp = this.parseDate(value);
      if (timestamp) {
        return timestamp;
      }
    }

    return 0;
  }

  private parseDate(value: any): number | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.getTime();
  }

  private resolveSerialNumber(device: any): string {
    return (
      device?.serial_number ||
      device?.serialNumber ||
      device?.serial_no ||
      device?.serialNo ||
      device?.serial ||
      ''
    );
  }

  private resolveModel(device: any): string {
    const modelCandidates = [
      device?.model_type,
      device?.model,
      device?.device_model,
      device?.equipment_type?.model,
      device?.equipment?.model
    ];

    return this.pickFirstString(modelCandidates);
  }

  private resolveManufacturer(device: any): string {
    const manufacturerCandidates = [
      device?.manufacturer,
      device?.device_manufacturer,
      device?.equipment?.manufacturer,
      device?.equipment_type?.manufacturer
    ];

    return this.pickFirstString(manufacturerCandidates);
  }

  private buildDeviceLabel(device: any): string {
    const serial = this.resolveSerialNumber(device);
    const model = this.resolveModel(device);
    const manufacturer = this.resolveManufacturer(device);
    const description = this.pickFirstString([
      device?.description,
      device?.name,
      device?.equipment_type?.description
    ]);

    const parts = [manufacturer, model, description].filter((part) => !!part);
    if (parts.length > 0) {
      return parts.join(' • ');
    }

    return serial || 'Unknown device';
  }

  private pickFirstString(values: Array<any>): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  }
}
